import {
	EXCLUSIVE_BRAND_DIY,
	NAVIGABLE_SECTION_CLASS_NAME,
	NAVIGABLE_SECTION_ID_ATTRIBUTE,
	NAVIGABLE_SECTION_IDS,
	PRODUCT_BUEN_FIN_TAG,
	PRODUCT_CYBER_DAYS_TAG,
	PRODUCT_EXCLUSIVE_TAG,
	PRODUCT_HOT_SALE_TAG,
	PRODUCT_TAGS_LABELS,
	SCROLL_TO_NAVIGABLE_SECTION_SPACING,
} from '@/components/content/ProductDetails/constants';
import { ProductType } from '@/data/types/Product';

type LabelTexType = PRODUCT_TAGS_LABELS | '';

export const getProductTagProps = (
	userData: Record<string, string>,
	product?: ProductType
): { badgePlp: string; tagBackgroundColor: string; labelText: LabelTexType } => {
	if (!!product && product.attributes) {
		const tagsList: string[] = [];
		product?.attributes?.map((attribute) => tagsList.push(attribute.identifier));

		// Highest priority
		if (tagsList.includes(PRODUCT_HOT_SALE_TAG)) {
			return {
				badgePlp: 'badge_plp-hs',
				tagBackgroundColor: 'tag_pdp-hs',
				labelText: PRODUCT_TAGS_LABELS.HOT_SALE,
			};
		}
		if (tagsList.includes(PRODUCT_BUEN_FIN_TAG)) {
			return {
				badgePlp: 'badge_plp-bf',
				tagBackgroundColor: 'tag_pdp-bf',
				labelText: PRODUCT_TAGS_LABELS.BUEN_FIN,
			};
		}
		if (tagsList.includes(PRODUCT_CYBER_DAYS_TAG)) {
			return {
				badgePlp: 'badge_plp-cd',
				tagBackgroundColor: 'tag_pdp-cd',
				labelText: PRODUCT_TAGS_LABELS.CYBER_DAYS,
			};
		}

		// Lowest priority
		const exclusiveBrands = userData[EXCLUSIVE_BRAND_DIY]?.split('|');
		const isExclusiveBrand = exclusiveBrands?.find(
			(exclusiveBrand: string) =>
				exclusiveBrand?.toUpperCase() === product.manufacturer?.toUpperCase()
		);
		if (isExclusiveBrand || tagsList.includes(PRODUCT_EXCLUSIVE_TAG)) {
			return {
				badgePlp: '',
				tagBackgroundColor: 'tag_pdp-ex',
				labelText: PRODUCT_TAGS_LABELS.EXCLUSIVE,
			};
		}
	}

	return { badgePlp: '', tagBackgroundColor: '', labelText: '' };
};

/**
 * Returns the offsetTop of the given element.
 */
export const getElementOffsetTop = (element: HTMLElement): number => {
	const offsetTop = element.offsetTop;

	if (element.offsetParent instanceof HTMLElement) {
		return offsetTop + getElementOffsetTop(element.offsetParent as HTMLElement);
	}

	return offsetTop;
};

/**
 * Returns the DOM element for the given navigable section ID.
 */
export const getNavigableSectionElement = (sectionId: NAVIGABLE_SECTION_IDS) =>
	document.querySelector<HTMLElement>(
		`.${NAVIGABLE_SECTION_CLASS_NAME}[${NAVIGABLE_SECTION_ID_ATTRIBUTE}="${sectionId}"]`
	);

/**
 * Calculates the vertical scroll offset adjustment for a navigable section.
 *
 * The adjustment accounts for fixed UI elements such as the header,
 * navigation bar, and product bar, and varies depending on:
 * - The target section
 * - The current screen size (small vs. medium and up)
 *
 * This value is used to ensure the section is fully visible after scrolling
 * and not hidden behind fixed layout components.
 *
 * @param sectionId - The unique identifier of the navigable section.
 * @param isSmScreen - Whether the current viewport matches a small screen breakpoint.
 * @param headerHeight - The height of the fixed header.
 * @param productBarHeight - The height of the product bar.
 * @param navigationBarHeight - The height of the navigation bar.
 * @returns The number of pixels to subtract from the scroll position.
 */
export const calculateScrollToNavigableSectionTopAdjustment = ({
	sectionId,
	isSmScreen,
	headerHeight,
	productBarHeight,
	navigationBarHeight,
}: {
	sectionId: NAVIGABLE_SECTION_IDS;
	isSmScreen: boolean;
	headerHeight: number;
	productBarHeight: number;
	navigationBarHeight: number;
}) => {
	let smScreenTopAdjustment = 0;
	let mdScreenTopAdjustment = 0;

	switch (sectionId) {
		case NAVIGABLE_SECTION_IDS.PRODUCT_DETAILS: {
			mdScreenTopAdjustment = 0;
			smScreenTopAdjustment = headerHeight;
			break;
		}
		case NAVIGABLE_SECTION_IDS.PRODUCT_BUY_BY_COLOR: {
			mdScreenTopAdjustment =
				navigationBarHeight - productBarHeight + SCROLL_TO_NAVIGABLE_SECTION_SPACING;
			smScreenTopAdjustment = headerHeight + mdScreenTopAdjustment;
			break;
		}
		default: {
			mdScreenTopAdjustment = navigationBarHeight + SCROLL_TO_NAVIGABLE_SECTION_SPACING;
			smScreenTopAdjustment =
				headerHeight + navigationBarHeight - productBarHeight + SCROLL_TO_NAVIGABLE_SECTION_SPACING;
			break;
		}
	}

	return isSmScreen ? smScreenTopAdjustment : mdScreenTopAdjustment;
};
